import tnaPeriodApi from '@/api/settings/tna-period';

const state = {
  periods: [],
  loading: false,
  error: null,
  // Pagination
  totalItems: 0,
  currentPage: 1,
  limit: 10,
  totalPages: 1
};

const getters = {
  periods: state => state.periods || [],
  loading: state => state.loading || false,
  error: state => state.error,
  // Pagination getters
  getTotalItems: state => state.totalItems,
  getCurrentPage: state => state.currentPage,
  getLimit: state => state.limit,
  getTotalPages: state => state.totalPages
};

const mutations = {
  SET_PERIODS(state, payload) {
    // Handle both array and paginated response
    if (payload && typeof payload === 'object' && 'data' in payload) {
      state.periods = Array.isArray(payload.data) ? payload.data : [];
      state.totalItems = payload.totalItems || 0;
      state.currentPage = payload.page || 1;
      state.limit = payload.limit || 10;
      state.totalPages = payload.totalPages || 1;
    } else {
      state.periods = Array.isArray(payload) ? payload : [];
    }
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  CLEAR_ERROR(state) {
    state.error = null;
  },
  SET_CURRENT_PAGE(state, page) {
    state.currentPage = page;
  },
  SET_LIMIT(state, limit) {
    state.limit = limit;
  },
  ADD_PERIOD(state, period) {
    state.periods.push(period);
  },
  UPDATE_PERIOD(state, { id, data }) {
    if (!id || !data) return;
    const index = state.periods.findIndex(p => p.id === id);
    if (index !== -1) {
      // Merge existing period with updated data
      state.periods.splice(index, 1, { ...state.periods[index], ...data });
    }
  },
  REMOVE_PERIOD(state, periodId) {
    const index = state.periods.findIndex(p => p.id === periodId);
    if (index !== -1) {
      state.periods.splice(index, 1);
    }
  }
};

const actions = {
  async getTNAPeriods({ commit }, params = {}) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    
    try {
      const response = await tnaPeriodApi.getTNAPeriods(params);
      
      if (response.data.httpCode === 200) {
        // Pass the full pagination response to mutation
        commit('SET_PERIODS', response.data.data || []);
        return response.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch TNA periods');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async createTNAPeriod({ commit }, data) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    
    try {
      const response = await tnaPeriodApi.createTNAPeriod(data);
      
      if (response.data.httpCode === 200 || response.data.httpCode === 201) {
        commit('ADD_PERIOD', response.data.data);
        return response.data;
      } else {
        throw new Error(response.data.message || 'Failed to create TNA period');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async updateTNAPeriod({ commit }, { id, data }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    
    try {
      const response = await tnaPeriodApi.updateTNAPeriod(id, data);
      
      if (response.data.httpCode === 200) {
        commit('UPDATE_PERIOD', { id, data });
        return response.data;
      } else {
        throw new Error(response.data.message || 'Failed to update TNA period');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
